/**
 * Email normalization utilities for vote integrity.
 * 
 * Gmail and Googlemail ignore dots in the local part of email addresses,
 * allowing users to create multiple accounts with the same inbox.
 * This utility normalizes Gmail addresses to prevent abuse.
 */

const GMAIL_DOMAINS = ['gmail.com', 'googlemail.com'];

/**
 * Normalize an email address for consistent identity matching.
 * 
 * For Gmail/Googlemail:
 * - Removes dots from local part
 * - Strips plus-addressing (e.g., user+tag@gmail.com → user@gmail.com)
 * 
 * For other domains:
 * - Only lowercases (dots are significant for most providers)
 * 
 * @param email - The email address to normalize
 * @returns The normalized email address
 */
export function normalizeEmail(email: string): string {
    const trimmed = email.trim().toLowerCase();
    const atIndex = trimmed.lastIndexOf('@');

    if (atIndex === -1) {
        // Invalid email, return as-is
        return trimmed;
    }

    const local = trimmed.slice(0, atIndex);
    const domain = trimmed.slice(atIndex + 1);

    if (!local || !domain) {
        return trimmed;
    }

    if (GMAIL_DOMAINS.includes(domain)) {
        // Remove dots and strip plus-addressing for Gmail
        const normalizedLocal = local.split('+')[0].replace(/\./g, '');
        return `${normalizedLocal}@${domain}`;
    }

    // For other domains, only lowercase (dots may be significant)
    return trimmed;
}
